import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import NotificationsBell from './NotificationsBell';
import { getLoggedInStudentId } from '../utils/sessionUser';
import { fetchStudentProfilePictureUrl } from '../utils/studentProfilePhoto';

export default function ProfileIcon({ navigation, user }) {
  const theme = useTheme();
  const propUrl = user?.profile_picture_url?.trim?.() || null;
  const [resolvedPhotoUrl, setResolvedPhotoUrl] = useState(propUrl);

  useEffect(() => {
    setResolvedPhotoUrl(propUrl);
  }, [propUrl]);

  const refreshProfilePhoto = useCallback(async () => {
    const loggedId = await getLoggedInStudentId();
    const fallbackId =
      user?.id != null && !Number.isNaN(Number(user.id)) ? Number(user.id) : null;
    const targetId = loggedId ?? fallbackId;
    if (targetId == null) {
      setResolvedPhotoUrl(propUrl);
      return;
    }
    const url = await fetchStudentProfilePictureUrl(targetId);
    setResolvedPhotoUrl(url ?? propUrl ?? null);
  }, [propUrl, user?.id]);

  useFocusEffect(
    useCallback(() => {
      void refreshProfilePhoto();
    }, [refreshProfilePhoto])
  );

  const handleProfilePress = () => {
    if (navigation && user?.id != null) {
      navigation.navigate('Profile', { userId: user.id });
    }
  };

  const formatGradeForDisplay = (grade) => {
    if (!grade) return 'No Grade';

    if (grade.includes('Degree') && grade.includes('Black Belt')) {
      const match = grade.match(/^(\d+(?:st|nd|rd|th)?\s+Degree)/i);
      if (match) {
        return match[1];
      }
    }

    return grade;
  };

  const styles = getStyles(theme);

  const initialLetter = (
    (user?.student_name && String(user.student_name).trim().charAt(0)) ||
    (user?.username && String(user.username).trim().charAt(0)) ||
    'U'
  ).toUpperCase();

  const showPhoto = Boolean(resolvedPhotoUrl);

  return (
    <View style={styles.headerRightContainer}>
      <NotificationsBell navigation={navigation} user={user} />
      <TouchableOpacity
        style={styles.container}
        onPress={handleProfilePress}
        activeOpacity={0.7}
      >
        <View style={styles.iconContainer}>
          {showPhoto ? (
            <Image
              source={{ uri: resolvedPhotoUrl }}
              style={styles.profileIconImage}
              contentFit="cover"
            />
          ) : (
            <View style={styles.profileIcon}>
              <Text style={styles.profileIconText}>{initialLetter}</Text>
            </View>
          )}
        </View>
        {user && (
          <View style={styles.textContainer}>
            <Text style={styles.nameText} numberOfLines={1}>
              {user.student_name || user.username || 'User'}
            </Text>
            <Text style={styles.gradeText} numberOfLines={1}>
              {formatGradeForDisplay(user.student_grade)}
            </Text>
          </View>
        )}
      </TouchableOpacity>
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    headerRightContainer: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
      borderRadius: theme.isDarkMode ? 0 : 8,
      paddingVertical: theme.isDarkMode ? 0 : 2,
      paddingHorizontal: theme.isDarkMode ? 0 : 4,
    },
    container: {
      alignItems: 'center',
      marginRight: 6,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    iconContainer: {
      marginBottom: 4,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    profileIcon: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: theme.colors.primary || '#d4af37',
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 2,
      borderColor: theme.colors.heading || '#d4af37',
    },
    profileIconText: {
      color: '#FFFFFF',
      fontSize: 18,
      fontWeight: 'bold',
    },
    profileIconImage: {
      width: 40,
      height: 40,
      borderRadius: 20,
      borderWidth: 2,
      borderColor: theme.colors.heading || '#d4af37',
    },
    textContainer: {
      alignItems: 'center',
      maxWidth: 80,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    nameText: {
      fontSize: 12,
      color: theme.colors.text,
      fontWeight: '600',
      textAlign: 'center',
    },
    gradeText: {
      fontSize: 11,
      color: '#d4af37',
      fontWeight: '800',
      textAlign: 'center',
      marginTop: 2,
    },
  });
